// Phase 3a-5: TanStack Query bindings for the OCI (Organizational Conflict of
// Interest) safe lane. The orchestration RPCs live on zou's ProjectDataService
// (RaiseOciFlag, RouteOciFlag, DecideOciFlag, ListOciFlags, OpenOciMitigation),
// proxied through sabaody. user_id/company_id are filled server-side from the
// JWT principal, so the browser passes only the domain fields.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
const OCI_KEY = 'oci-flags';
/** Load a project's OCI flags for the workspace list. */ export function useOciFlags(projectId) {
    return useQuery({
        queryKey: [
            OCI_KEY,
            projectId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listOciFlags({
                projectId
            });
            return resp.ociFlags ?? [];
        },
        enabled: !!projectId
    });
}
function useOciMutation(fn, failMsg) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    OCI_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : failMsg);
        }
    });
}
export function useRaiseOciFlag() {
    return useOciMutation((args)=>projectDataClient.raiseOciFlag({
            projectId: args.projectId,
            signalKind: args.signalKind,
            title: args.title,
            description: args.description ?? ''
        }), 'Failed to raise OCI flag');
}
export function useRouteOciFlag() {
    return useOciMutation((args)=>projectDataClient.routeOciFlag({
            ociFlagId: args.ociFlagId,
            functionalOwner: args.functionalOwner
        }), 'Failed to route OCI flag');
}
export function useDecideOciFlag() {
    return useOciMutation((args)=>projectDataClient.decideOciFlag({
            ociFlagId: args.ociFlagId,
            decision: args.decision,
            reason: args.reason
        }), 'Failed to decide OCI flag');
}
export function useOpenOciMitigation() {
    return useOciMutation((args)=>projectDataClient.openOciMitigation({
            ociFlagId: args.ociFlagId,
            firewallPlan: args.firewallPlan,
            restrictedUserIds: args.restrictedUserIds,
            restrictedArtifactIds: args.restrictedArtifactIds
        }), 'Failed to open OCI mitigation');
}
/** The SDS §4 functional-owner roster the route dropdown offers. Mirrors the
 *  server-side validFunctionalOwners (RouteDiscrepancy / RouteOciFlag). */ export const OCI_FUNCTIONAL_OWNERS = [
    'Delivery',
    'Contracts',
    'Finance',
    'Subcontracts',
    'HrOnboarding',
    'Quality',
    'Deliverables',
    'GrowthHandoff'
];
